import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { EmptyState, Loading, PageHeader, Section } from '../../components/ui/index.jsx';
import { fmtDateTime } from '../../utils/format.js';
import { RegisteredAgo, SOURCE, SiteStatus, defangDomain } from './lookalikeShared.jsx';

const VIEWS = [['needs', 'Needs a look'], ['watching', 'Watching'], ['scam', 'Scam sites'], ['all', 'Everything we found']];

function AddModal({ organisationId, onClose }) {
  const navigate = useNavigate();
  const [site, setSite] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true); setError('');
    try {
      const r = await api(`/portal/organisations/${organisationId}/lookalikes`, { method: 'POST', body: { site } });
      navigate(`/company/lookalikes/${r.id}`, { state: { notice: 'Added. We’re checking it now — a screenshot takes a minute or two.' } });
    } catch (err) { setError(err.data?.fields?.site || err.message); setBusy(false); }
  };
  return (
    <Modal title="Check a website" onClose={onClose} onSubmit={save} busy={busy}
      description="A site you’ve heard about or been sent, or a listing on Eventbrite, Meetup, Facebook or LinkedIn events. We check it safely, take a screenshot, and compare it with your own site."
      footer={<ModalActions submitLabel="Check it" busyLabel="Adding…" busy={busy} onCancel={onClose} />}>
      <Field label="Website address" value={site} onChange={(e) => setSite(e.target.value)} error={error} placeholder="techsummit-tickets.com, or an Eventbrite listing link" autoCapitalize="none" autoFocus />
    </Modal>
  );
}

/** Websites made to look like the company's: copies, sites using its name, and ones to watch. */
export default function Lookalikes() {
  const { organisation } = useAuth();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') || 'needs';
  const page = Number(params.get('page')) || 1;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const location = useLocation();
  const [notice] = useState(location.state?.notice || '');

  useEffect(() => {
    if (!organisation) return;
    setData(null);
    api(`/portal/organisations/${organisation.id}/lookalikes?view=${view}&page=${page}`).then(setData).catch((err) => setError(err.message));
  }, [organisation, view, page]);

  const header = (
    <PageHeader title="Fake websites"
      actions={<button type="button" className="btn btn-secondary btn-small" onClick={() => setAdding(true)}>Check a website</button>}
      subtitle="Websites made to look like yours. Every day we look up typos and variations of your domains and search new HTTPS certificates for your names, then check what’s on each one — safely, with a screenshot — and compare it with your own site. You decide what each is." />
  );
  if (error) return <div className="page">{header}<Alert tone="danger">{error}</Alert></div>;

  return (
    <div className="page">
      {header}
      {notice && <Alert tone="success">{notice}</Alert>}
      {data && !data.has_verified_domain && <Alert tone="warn">Verify one of your domains first: we look for sites made to look like your verified domains. <Link to="/company">Verify a domain</Link></Alert>}
      {data?.counts.needs > 0 && view !== 'needs' && (
        <Alert tone="danger">{data.counts.needs} site{data.counts.needs === 1 ? '' : 's'} copying you or using your name {data.counts.needs === 1 ? 'needs' : 'need'} a look. <Link to="?view=needs">Show {data.counts.needs === 1 ? 'it' : 'them'}</Link></Alert>
      )}

      <div className="segmented add-kinds" role="tablist" aria-label="Which sites">
        {VIEWS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={view === k} className={view === k ? 'is-active' : ''} onClick={() => setParams({ view: k })}>
            {label}{data && k !== 'all' && data.counts[k] > 0 ? ` (${data.counts[k]})` : ''}
          </button>
        ))}
      </div>

      <Section flush={!!data?.sites.length}>
        {!data ? <Loading /> : data.sites.length === 0 ? (
          <EmptyState title={view === 'needs' ? 'Nothing needs a look' : 'None here'}>
            {view === 'needs'
              ? (data.counts.total ? `We’re watching ${data.counts.total} lookalike domain${data.counts.total === 1 ? '' : 's'}; none of them copies you or uses your name right now.` : 'We haven’t found any lookalike sites yet. The first search runs within a day of verifying a domain.')
              : 'Nothing in this list.'}
          </EmptyState>
        ) : (
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Website</th><th>What it is</th><th>Registered</th><th>How we found it</th><th>Last checked</th></tr></thead>
              <tbody>
                {data.sites.map((s) => (
                  <tr key={s.id}>
                    <td data-label="Website">
                      <Link className="strong-link" to={`/company/lookalikes/${s.id}`}>
                        {s.listing ? <><strong>{s.listing.platform} listing</strong> <span className="mono small">{defangDomain(s.domain)}</span></> : <span className="mono">{defangDomain(s.domain)}</span>}
                      </Link>
                      {(s.listing?.name || s.title) && <div className="muted small">{s.listing?.name || s.title}{s.listing?.organiser?.name ? ` · by ${s.listing.organiser.name}` : ''}</div>}
                    </td>
                    <td data-label="What it is"><SiteStatus site={s} /></td>
                    <td data-label="Registered" className="small nowrap">{s.listing ? <span className="muted">Listing</span> : <RegisteredAgo at={s.registered_at} short />}{s.registrar && <div className="muted small">{s.registrar}</div>}</td>
                    <td data-label="How we found it" className="small">{SOURCE[s.source]}{s.looks_like && <span className="muted"> · like {s.looks_like}</span>}{s.found_query && <div className="muted small">for {s.found_query}</div>}</td>
                    <td data-label="Last checked" className="nowrap small muted">{s.checked_at ? fmtDateTime(s.checked_at) : 'Not yet'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {data && (page > 1 || data.has_more) && (
        <div className="pager">
          {page > 1 && <button type="button" className="btn btn-secondary btn-small" onClick={() => setParams({ view, page: String(page - 1) })}>Previous</button>}
          <span className="muted small">Page {page}</span>
          {data.has_more && <button type="button" className="btn btn-secondary btn-small" onClick={() => setParams({ view, page: String(page + 1) })}>Next</button>}
        </div>
      )}
      {adding && <AddModal organisationId={organisation.id} onClose={() => setAdding(false)} />}
    </div>
  );
}
